'use client';

import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { ActivityData } from '@/types/websocket';
import { WebSocketConnectionState } from '@/types/websocket';
import { isActivityRunning } from '@/utils/activity-helpers';

import { useWebSocketStore } from './provider';
import type { UseRecentActivitiesResult, UseWebSocketStatusResult } from './types';

// ------------- Connection Status Hook -------------

/**
 * Hook to access WebSocket connection status.
 * Following official Zustand v5 patterns with useShallow for optimal re-renders.
 *
 * @throws Error if used outside of WebSocketProvider
 *
 * Usage:
 * ```tsx
 * const { isConnected, isReconnecting, hasError, lastError } = useWebSocketStatus();
 *
 * if (hasError) {
 *   console.error('WebSocket error:', lastError);
 * }
 * ```
 */
export function useWebSocketStatus(): UseWebSocketStatusResult {
  const { connectionState, lastError, reconnectAttempts } = useWebSocketStore(
    useShallow(state => ({
      connectionState: state.connectionState,
      lastError: state.lastError,
      reconnectAttempts: state.reconnectAttempts,
    })),
  );

  return {
    connectionState,
    isConnected: connectionState === WebSocketConnectionState.CONNECTED,
    isReconnecting: connectionState === WebSocketConnectionState.RECONNECTING,
    hasError: connectionState === WebSocketConnectionState.ERROR || lastError !== null,
    lastError,
    reconnectAttempts,
  };
}

// ------------- Recent Activities Hook -------------

/**
 * Hook to access recent activities from WebSocket.
 * Following official Zustand v5 patterns with useShallow for optimal re-renders.
 *
 * @throws Error if used outside of WebSocketProvider
 *
 * Usage:
 * ```tsx
 * const { activities, latestActivity, hasActivityInProgress } = useRecentActivities();
 *
 * if (hasActivityInProgress) {
 *   // Show activity indicator
 * }
 * ```
 */
export function useRecentActivities(): UseRecentActivitiesResult & { isLoading: boolean } {
  const { activities, isHydrated } = useWebSocketStore(
    useShallow(state => ({
      activities: state.recentActivities,
      isHydrated: state.isHydrated,
    })),
  );

  return useMemo(() => {
    const latestActivity = activities.length > 0 ? activities[0] : null;
    // Not a bare IN_PROGRESS check: a record orphaned by a killed worker would
    // otherwise keep the notification dot lit for good.
    const hasActivityInProgress = activities.some(activity => isActivityRunning(activity));

    return {
      activities,
      latestActivity: latestActivity ?? null,
      hasActivityInProgress,
      isLoading: isHydrated === false,
    };
  }, [activities, isHydrated]);
}

// ------------- Individual Selectors -------------

/**
 * Get connection state only.
 * @throws Error if used outside of WebSocketProvider
 */
export function useWebSocketConnectionState(): WebSocketConnectionState {
  return useWebSocketStore(state => state.connectionState);
}

/**
 * Check if WebSocket is connected.
 * @throws Error if used outside of WebSocketProvider
 */
export function useIsWebSocketConnected(): boolean {
  const connectionState = useWebSocketStore(state => state.connectionState);
  return connectionState === WebSocketConnectionState.CONNECTED;
}

/**
 * Get recent activities list.
 * @throws Error if used outside of WebSocketProvider
 */
export function useWebSocketActivities(): ActivityData[] {
  return useWebSocketStore(state => state.recentActivities);
}

/**
 * Get last error message.
 * @throws Error if used outside of WebSocketProvider
 */
export function useWebSocketLastError(): string | null {
  return useWebSocketStore(state => state.lastError);
}

/**
 * Check if WebSocket store is hydrated.
 * @throws Error if used outside of WebSocketProvider
 */
export function useIsWebSocketHydrated(): boolean {
  return useWebSocketStore(state => state.isHydrated);
}

/**
 * Get the addActivity action for adding activities to the store.
 * Useful for testing/development purposes.
 * @throws Error if used outside of WebSocketProvider
 */
export function useAddActivity(): (activity: ActivityData) => void {
  return useWebSocketStore(state => state.addActivity);
}
